import React, { useState } from 'react';
import { useQuery, useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Id } from '@/convex/_generated/dataModel';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/hooks/useAuth';
import { useWeb3Auth } from '@/providers/Web3AuthProvider';
import { useToast } from '@/components/ui/use-toast';
import { UsersList } from './UsersList';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Loader } from '@/components/ui/loader';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface User {
	_id: Id<'users'>;
	email: string;
	name: string;
	role?: string;
	isAdmin?: boolean;
	isActive?: boolean;
	walletAddress?: string;
	initialApiKey?: string;
	profileKey?: string;
	profileTitle?: string;
	lastWarningAcknowledgment?: string;
	lastLoginAt?: string;
	createdAt?: string;
	updatedAt?: string;
	tokenIdentifier: string;
}

export function UsersManagement() {
	const [searchTerm, setSearchTerm] = useState('');
	const [newUserEmail, setNewUserEmail] = useState('');
	const [newUserName, setNewUserName] = useState('');
	const [isCreating, setIsCreating] = useState(false);
	const { user } = useAuth();
	const { getMemberAuthToken } = useWeb3Auth();
	const { toast } = useToast();
	const createUser = useAction(api.users.createWithClerkAuthorized);

	const users = useQuery(
		api.users.list,
		user?.id && user?.initialApiKey
			? {
					requestingUserId: user.id as unknown as string,
					apiKey: user.initialApiKey ?? '',
				}
			: 'skip',
	) as User[] | undefined;

	const handleCreateUser = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!newUserEmail || !newUserName) {
			toast({
				title: '❌ Validation Error',
				description: 'Please fill in all fields',
				variant: 'destructive',
				duration: 3000,
			});
			return;
		}

		try {
			setIsCreating(true);

			if (!getMemberAuthToken) throw new Error('MEMBER_SIGNED_AUTH_REQUIRED');
			await createUser({
				email: newUserEmail,
				name: newUserName,
				memberToken: await getMemberAuthToken(),
			});

			toast({
				title: '✨ User Created',
				description: 'New user has been created successfully',
				duration: 3000,
			});

			setNewUserEmail('');
			setNewUserName('');
		} catch (error) {
			toast({
				title: '❌ Error',
				description:
					error instanceof Error ? error.message : 'Failed to create user',
				variant: 'destructive',
				duration: 3000,
			});
		} finally {
			setIsCreating(false);
		}
	};

	const filteredUsers = users?.filter((user: User) => {
		if (!searchTerm) return true;

		const searchLower = searchTerm.toLowerCase();
		return (
			user.email?.toLowerCase().includes(searchLower) ||
			user.name?.toLowerCase().includes(searchLower) ||
			user.role?.toLowerCase().includes(searchLower)
		);
	});

	if (!user) {
		return (
			<div className="container mx-auto p-6">
				<Card className="border-orange-500/50">
					<CardHeader>
						<CardTitle className="text-2xl font-bold mb-2 text-orange-500">
							Authentication Required
						</CardTitle>
					</CardHeader>
					<CardContent>
						<Alert>
							<AlertTitle>Please Sign In</AlertTitle>
							<AlertDescription>
								You need to be signed in to access this page.
							</AlertDescription>
						</Alert>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (!users) {
		return (
			<div className="container mx-auto p-6">
				<Card>
					<CardContent>
						<div className="flex items-center justify-center min-h-[600px] py-32">
							<Loader message="LOADING USERS..." />
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="container mx-auto p-6 pb-80">
			<div className="grid gap-6">
				{/* Create User Card */}
				<Card className="glow-card">
					<CardHeader>
						<CardTitle className="text-2xl font-bold text-pink-500">
							Create New User
						</CardTitle>
					</CardHeader>
					<CardContent>
						<form onSubmit={handleCreateUser} className="space-y-4">
							<div className="grid gap-4">
								<div className="grid gap-2">
									<Label htmlFor="email" className="text-gray-300">
										Email
									</Label>
									<Input
										id="email"
										type="email"
										placeholder="user@example.com"
										value={newUserEmail}
										onChange={(e) => setNewUserEmail(e.target.value)}
										className="bg-white/5 border-gray-700"
									/>
								</div>
								<div className="grid gap-2">
									<Label htmlFor="name" className="text-gray-300">
										Name
									</Label>
									<Input
										id="name"
										type="text"
										placeholder="John Doe"
										value={newUserName}
										onChange={(e) => setNewUserName(e.target.value)}
										className="bg-white/5 border-gray-700"
									/>
								</div>
							</div>
							<Button
								type="submit"
								disabled={isCreating}
								className={cn(
									'w-full bg-gradient-to-b from-pink-400 to-pink-600 text-white hover:from-pink-500 hover:to-pink-700',
									isCreating && 'opacity-50 cursor-not-allowed',
								)}
							>
								{isCreating ? 'Creating...' : 'Create User'}
							</Button>
						</form>
					</CardContent>
				</Card>

				{/* Users List Card */}
				<Card className="glow-card">
					<CardHeader>
						<div className="space-y-2">
							<div className="flex items-center gap-2">
								<span className="text-3xl font-bold text-pink-500">
									Users Management
								</span>
							</div>
							<p className="text-lg text-gray-500">
								Manage user accounts and permissions
							</p>
						</div>
					</CardHeader>
					<CardContent>
						<div className="flex justify-end mb-6">
							<Input
								type="search"
								placeholder="Search users..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="max-w-xs bg-white/5 border-gray-700"
							/>
						</div>
						{filteredUsers && (
							<UsersList users={filteredUsers} requestingUserId={user.id} />
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
